<template>
  <article class="retreat-page">
    <EventHero :event="event" />
    <EventSectionNav :items="navigation" />
    <EventSummary :event="event" heading="Summary" kicker="Corporate Retreat" />
    <EventStats
      :items="event.atAGlance"
      title="The Essentials"
      kicker="Retreat Information"
      section-id="essentials"
    />
    <EventStats
      :items="event.tripByNumbers"
      title="By the Numbers"
      kicker="The Retreat in Numbers"
      section-id="by-the-numbers"
    />
    <ExperienceDNA :items="event.experienceDNA" />
    <JourneyStrip v-if="storyAvailable" :items="publishedJourney" />
    <TravelStoriesSection
      v-if="storyAvailable"
      :stories="travelStories"
      :event-title="event.title"
    />
    <section v-if="inlineChapters.length" class="story-section" aria-label="Published retreat chapters">
      <v-container>
        <StoryChapter
          v-for="chapter in inlineChapters"
          :key="chapter.id"
          :chapter="chapter"
          :entity-by-id="entityById"
        />
      </v-container>
    </section>
    <EntitySection
      :items="visiblePlaces"
      :entity-by-id="entityById"
      section-id="places"
      title="Places We Explored"
      kicker="Revealed Along the Journey"
    />
    <EntitySection
      :items="visibleRestaurants"
      :entity-by-id="entityById"
      :event="event"
      detail-enabled
      section-id="dining"
      title="Dining Along the Way"
      kicker="Meals Behind the Stories"
    />
    <EntitySection
      :items="visibleHotels"
      :entity-by-id="entityById"
      section-id="hotels"
      title="Where We Stayed"
      kicker="Home Along the Way"
    />
    <EntitySection
      :items="visibleActivities"
      :entity-by-id="entityById"
      :event="event"
      detail-enabled
      section-id="activities"
      title="Activities and Team Experiences"
      kicker="Adventures as They Unfold"
    />
    <CrewSection :crew="event.crew" />
    <MeritBadgeSection :badges="visibleBadges" />
    <EventGallery
      v-if="storyAvailable && visibleGallery.length"
      :media="visibleGallery"
      :title="`${event.title} Moments`"
    />
  </article>
</template>

<script>
import { isRetreatStoryAvailable, isRetreatStoryComplete, publishedChapters } from '../../../data/reckon-way/sortEventEntities';
import EventHero from '../event/EventHero.vue';
import EventSectionNav from '../event/EventSectionNav.vue';
import EventSummary from '../event/EventSummary.vue';
import EventStats from '../stats/EventStats.vue';
import ExperienceDNA from '../stats/ExperienceDNA.vue';
import JourneyStrip from '../story/JourneyStrip.vue';
import StoryChapter from '../story/StoryChapter.vue';
import TravelStoriesSection from '../story/TravelStoriesSection.vue';
import EntitySection from '../entities/EntitySection.vue';
import CrewSection from '../people/CrewSection.vue';
import MeritBadgeSection from '../badges/MeritBadgeSection.vue';
import EventGallery from '../media/EventGallery.vue';

export default {
  name: 'RetreatEventPage',
  components: {
    EventHero,
    EventSectionNav,
    EventSummary,
    EventStats,
    ExperienceDNA,
    JourneyStrip,
    StoryChapter,
    TravelStoriesSection,
    EntitySection,
    CrewSection,
    MeritBadgeSection,
    EventGallery,
  },
  props: { event: { type: Object, required: true }, entityById: { type: Object, required: true } },
  computed: {
    storyAvailable() { return isRetreatStoryAvailable(this.event); },
    storyComplete() { return isRetreatStoryComplete(this.event); },
    chapters() { return publishedChapters(this.event); },
    travelStories() { return this.event.travelStories?.length ? this.event.travelStories : this.chapters; },
    inlineChapters() { return this.chapters.filter((chapter) => !chapter.route && (chapter.body || chapter.entityReferences?.length)); },
    publishedChapterIds() { return new Set(this.chapters.map((chapter) => chapter.id)); },
    publishedEntityIds() {
      return new Set(this.chapters.flatMap((chapter) => (chapter.entityReferences || []).map((item) => item.entityId)));
    },
    publishedBadgeIds() { return new Set(this.chapters.flatMap((chapter) => chapter.badgeIds || [])); },
    publishedMediaIds() {
      return new Set(this.chapters.flatMap((chapter) => [
        ...(chapter.gallery || []).map((asset) => asset.id),
        ...(chapter.entityReferences || []).flatMap((item) => item.relatedMediaIds || []),
      ]).filter(Boolean));
    },
    publishedJourney() {
      return this.chapters.map((chapter) => ({
        id: chapter.id,
        label: chapter.dayLabel || `Chapter ${chapter.chapterNumber}`,
        value: chapter.title,
      }));
    },
    visiblePlaces() { return this.visibleItems('places'); },
    visibleRestaurants() { return this.visibleItems('restaurants'); },
    visibleHotels() { return this.visibleItems('hotels'); },
    visibleActivities() { return this.visibleItems('activities'); },
    visibleBadges() {
      if (!this.storyAvailable) return [];
      return (this.event.meritBadges || []).filter((badge) => this.storyComplete
        || this.publishedBadgeIds.has(badge.id || badge.slug)
        || this.publishedChapterIds.has(badge.chapterId));
    },
    visibleGallery() {
      if (!this.storyAvailable) return [];
      const eventMedia = (this.event.gallery || []).filter(Boolean).filter((asset) => {
        if (this.storyComplete) return !asset.hiddenUntilPublished;
        if (asset.chapterId) return this.publishedChapterIds.has(asset.chapterId);
        if (asset.hiddenUntilPublished) return this.publishedMediaIds.has(asset.id);
        return true;
      });
      const chapterMedia = this.chapters.flatMap((chapter) => chapter.gallery || []);
      const seen = new Set();
      return [...eventMedia, ...chapterMedia].filter((asset) => {
        const key = asset.id || asset.src;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    navigation() {
      return [
        { id: 'overview', label: 'Summary', show: true },
        { id: 'essentials', label: 'Essentials', show: this.event.atAGlance?.length },
        { id: 'by-the-numbers', label: 'By the Numbers', show: this.event.tripByNumbers?.length },
        { id: 'experience-dna', label: 'Category Mix', show: this.event.experienceDNA?.length },
        { id: 'journey', label: 'Journey', show: this.storyAvailable && this.publishedJourney.length },
        { id: 'stories', label: 'Travel Stories', show: this.storyAvailable && this.travelStories.length },
        { id: 'places', label: 'Places', show: this.visiblePlaces.length },
        { id: 'dining', label: 'Dining', show: this.visibleRestaurants.length },
        { id: 'hotels', label: 'Hotels', show: this.visibleHotels.length },
        { id: 'activities', label: 'Activities', show: this.visibleActivities.length },
        { id: 'crew', label: 'Crew', show: this.event.crew?.length },
        { id: 'badges', label: 'Badges', show: this.visibleBadges.length },
        { id: 'gallery', label: 'Gallery', show: this.storyAvailable && this.visibleGallery.length },
      ].filter((item) => item.show);
    },
  },
  methods: {
    visibleItems(sectionKey) {
      if (!this.storyAvailable) return [];
      return (this.event[sectionKey] || []).filter((item) => {
        if (item.hiddenUntilPublished && !this.storyComplete) {
          return this.publishedChapterIds.has(item.chapterId) || this.publishedEntityIds.has(item.entityId);
        }
        if (this.storyComplete) return true;
        return this.publishedChapterIds.has(item.chapterId) || this.publishedEntityIds.has(item.entityId);
      });
    },
  },
};
</script>

<style scoped>
.retreat-page { background: #050505; }
.story-section { background: #0b0b0b; border-top: 1px solid #282828; scroll-margin-top: 68px; }
</style>
